'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Center, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  Button,
  IconButton,
  ImageWithSkeleton,
  LegacyDialog,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useAssetTableEndpointWebpages } from '../../../hooks/api/useAssetTableRelations';
import { useWebpageDetails } from '../../../hooks/api/useWebpageDetails';
import { getWebpageTabInput } from './asset-tab-inputs';
import {
  useAssetsTableContext,
  type AssetsTableScreenshotPage,
  type AssetsTableScreenshotRequest,
} from './AssetsTableContext';

const DIALOG_MAX_WIDTH = '960px';
const SCREENSHOT_HEIGHT = '540px';

function useScreenshotPages(request: AssetsTableScreenshotRequest) {
  const endpointId = request.source === 'endpoint' ? request.endpointId : '';
  const { data } = useAssetTableEndpointWebpages({
    endpointId,
    enabled: request.source === 'endpoint',
  });

  return useMemo<AssetsTableScreenshotPage[]>(() => {
    const relatedPages = (data?.webpages ?? [])
      .filter((webpage) => webpage.id !== request.page.id)
      .map((webpage) => ({
        id: webpage.id,
        url: webpage.url,
        signedUrl: null,
      }));

    return [request.page, ...relatedPages];
  }, [data?.webpages, request.page]);
}

function WebpageScreenshotDialogBody({
  request,
  onClose,
}: {
  request: AssetsTableScreenshotRequest;
  onClose: () => void;
}) {
  const t = useTranslations('proxy.assets.table');
  const { onOpenTab, onOpenTabContextMenu } = useAssetsTableContext();
  const pages = useScreenshotPages(request);
  const [pageIndex, setPageIndex] = useState(0);
  const currentPage = pages[Math.min(pageIndex, pages.length - 1)];
  const { data: webpageDetails, isLoading: isLoadingDetails } =
    useWebpageDetails({
      webpageId: currentPage.id,
      enabled: currentPage.signedUrl === null,
    });
  const signedUrl = currentPage.signedUrl ?? webpageDetails?.signedUrl ?? null;
  const tab = getWebpageTabInput({ id: currentPage.id, url: currentPage.url });
  const hasMultiplePages = pages.length > 1;

  return (
    <VStack gap={3} alignItems="stretch">
      <HStack justifyContent="space-between" gap={3}>
        <LegacyDialog.Title asChild>
          <Button
            variant="link"
            size="sm"
            minW={0}
            justifyContent="flex-start"
            onClick={() => {
              onClose();
              onOpenTab(tab);
            }}
            onContextMenu={(event) => onOpenTabContextMenu({ event, tab })}
          >
            <BodyDefault truncate>{currentPage.url}</BodyDefault>
          </Button>
        </LegacyDialog.Title>
        <HStack gap={1} flexShrink={0}>
          {hasMultiplePages && (
            <>
              <MetaDefault color="fg.muted">
                {t('screenshotPosition', {
                  current: pageIndex + 1,
                  total: pages.length,
                })}
              </MetaDefault>
              <Tooltip title={t('previousScreenshot')}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={t('previousScreenshot')}
                  disabled={pageIndex === 0}
                  onClick={() => setPageIndex((index) => index - 1)}
                >
                  <ArrowLeft01Icon size={14} strokeWidth={1.5} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('nextScreenshot')}>
                <IconButton
                  variant="ghost"
                  size="xs"
                  aria-label={t('nextScreenshot')}
                  disabled={pageIndex >= pages.length - 1}
                  onClick={() => setPageIndex((index) => index + 1)}
                >
                  <ArrowRight01Icon size={14} strokeWidth={1.5} />
                </IconButton>
              </Tooltip>
            </>
          )}
          <LegacyDialog.CloseTrigger asChild>
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={t('closeScreenshot')}
            >
              <Cancel01Icon size={14} strokeWidth={1.5} />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </HStack>
      </HStack>
      <Box
        h={SCREENSHOT_HEIGHT}
        borderRadius="md"
        overflow="hidden"
        borderWidth={1}
        borderColor="border.subtle"
        bg="bg.subtle"
      >
        {signedUrl || isLoadingDetails ? (
          <ImageWithSkeleton
            key={currentPage.id}
            src={signedUrl}
            alt={currentPage.url}
            errorMessage={t('screenshotLoadError')}
            objectFit="contain"
          />
        ) : (
          <Center h="full">
            <MetaDefault color="fg.muted">{t('noScreenshot')}</MetaDefault>
          </Center>
        )}
      </Box>
    </VStack>
  );
}

export function WebpageScreenshotDialog({
  request,
  onClose,
}: {
  request: AssetsTableScreenshotRequest | null;
  onClose: () => void;
}) {
  return (
    <LegacyDialog.Root
      open={request !== null}
      lazyMount
      unmountOnExit
      onOpenChange={({ open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content p={4} w="full" maxW={DIALOG_MAX_WIDTH}>
          {request && (
            <WebpageScreenshotDialogBody
              key={`${request.source}:${request.page.id}`}
              request={request}
              onClose={onClose}
            />
          )}
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
